import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import type { LocalProjectDetail, ProjectSyncState } from '@/types/project';

export interface ProjectContextValue {
  projectId: number | null;
  detail: LocalProjectDetail | null;
  syncState: ProjectSyncState;
  syncError: string | null;
  selectProject: (detail: LocalProjectDetail) => void;
  setSyncState: (state: ProjectSyncState, error?: string | null) => void;
  clearProject: () => void;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [detail, setDetail] = useState<LocalProjectDetail | null>(null);
  const [syncState, setSyncStateValue] = useState<ProjectSyncState>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);

  const selectProject = useCallback((selectedDetail: LocalProjectDetail) => {
    setProjectId(selectedDetail.project_id);
    setDetail(selectedDetail);
    setSyncStateValue('idle');
    setSyncError(null);
  }, []);

  const setSyncState = useCallback((state: ProjectSyncState, error: string | null = null) => {
    setSyncStateValue(state);
    setSyncError(error);
  }, []);

  const clearProject = useCallback(() => {
    setProjectId(null);
    setDetail(null);
    setSyncStateValue('idle');
    setSyncError(null);
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projectId,
        detail,
        syncState,
        syncError,
        selectProject,
        setSyncState,
        clearProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
}
